import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';
import { subscribeToPush, unsubscribeFromPush } from '../services/webPush';

const AuthContext = createContext(null);

// iter246 — small helper so a Web Push subscription attempt after login
// never blocks the auth flow (permission requests are user-initiated on
// the notification bell later; this call is a silent best-effort so
// already-permitted browsers get subscribed immediately).
const _tryPushSubscribe = (token) => {
  try {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return; // never prompt from here
    subscribeToPush({ token }).catch(() => {});
  } catch (_) { /* silent */ }
};

const _tryPushUnsubscribe = (token) => {
  try { unsubscribeFromPush({ token }).catch(() => {}); } catch (_) { /* silent */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // iter289 — Stable identity for updateUser via functional updater form.
  // Prevents downstream consumers (PermissionContext.refresh) from getting
  // a new function identity every render, which was driving an infinite
  // login/refresh loop.
  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...updates };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    // iter246 — invalidate the browser push subscription BEFORE we drop
    // the token so the /api/push/unsubscribe call still authenticates.
    _tryPushUnsubscribe(localStorage.getItem('token'));
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear all persisted filters
    Object.keys(localStorage)
      .filter(k => k.startsWith('crm_filters_') || k.startsWith('leads_'))
      .forEach(k => localStorage.removeItem(k));
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          // Verify token is still valid — response now includes permissions
          const response = await authAPI.getMe();
          const freshUser = response.data;
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        } catch (error) {
          console.error('Auth verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token: newToken, user: newUser, permissions } = response.data;

    // Merge permissions into user object so PermissionContext can read them
    const userWithPerms = { ...newUser, permissions: permissions || {} };

    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userWithPerms));
    setToken(newToken);
    setUser(userWithPerms);
    _tryPushSubscribe(newToken);

    return response.data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    const { token: newToken, user: newUser } = response.data;

    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
    _tryPushSubscribe(newToken);

    return response.data;
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAuthenticated: !!token,
      login,
      register,
      logout,
      updateUser,
    }),
    [user, token, loading, login, register, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
